/**
 * A custom element that turns the announcement bar into an infinite,
 * direction-aware (RTL/LTR) marquee.
 *
 * It measures the original group of announcements, clones it enough times
 * to cover the container twice, then drives a CSS animation that shifts
 * the track by exactly one group width for a seamless loop.
 *
 * Respects `prefers-reduced-motion` by rendering a static, centered bar.
 */
class AnnouncementMarquee extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Last measured widths, to skip redundant rebuilds on noop resizes */
  #lastKey = '';

  connectedCallback() {
    this.#reducedMotion.addEventListener('change', this.#handleChange);
    this.#resizeObserver = new ResizeObserver(this.#handleChange);
    this.#resizeObserver.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    this.#reducedMotion.removeEventListener('change', this.#handleChange);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#lastKey = '';
  }

  #handleChange = () => this.#update();

  #update() {
    const track = this.querySelector('.announcement-marquee__track');
    const group = this.querySelector('.announcement-marquee__group');
    if (!track || !group) return;

    if (this.#reducedMotion.matches) {
      if (this.#lastKey !== 'static') {
        this.#lastKey = 'static';
        this.#removeClones(track);
        this.classList.add('announcement-marquee--static');
        track.style.removeProperty('--marquee-shift');
        track.style.removeProperty('--marquee-duration');
      }
      return;
    }

    this.classList.remove('announcement-marquee--static');

    const containerWidth = this.getBoundingClientRect().width;
    const groupWidth = group.getBoundingClientRect().width;
    if (groupWidth === 0 || containerWidth === 0) return;

    const key = `${Math.round(containerWidth)}|${Math.round(groupWidth)}`;
    if (key === this.#lastKey) return;
    this.#lastKey = key;

    this.#removeClones(track);

    // Enough copies so that content covers the container plus one full
    // group that can scroll out of view before the animation loops.
    const copies = Math.max(1, Math.ceil(containerWidth / groupWidth) + 1);
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < copies; i++) {
      const clone = group.cloneNode(true);
      if (!(clone instanceof HTMLElement)) continue;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-marquee-clone', '');
      // Clones must not be announced, focusable, or picked up by the theme editor
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      fragment.appendChild(clone);
    }
    track.appendChild(fragment);

    const dir = this.closest('[dir]')?.getAttribute('dir') || document.documentElement.getAttribute('dir') || 'ltr';
    // In LTR the track scrolls toward the left (negative X); in RTL toward the right.
    const shift = dir === 'rtl' ? groupWidth : -groupWidth;

    // px per second; the section sets --marquee-speed from its "speed" setting.
    const speed = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-speed')) || 40;

    track.style.setProperty('--marquee-shift', `${shift}px`);
    track.style.setProperty('--marquee-duration', `${(groupWidth / speed).toFixed(2)}s`);
  }

  /** @param {Element} track */
  #removeClones(track) {
    track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
